import { useRef } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { Carousel } from '@mantine/carousel';
import { Loader } from '@mantine/core';
import Autoplay from 'embla-carousel-autoplay';
import Confetti from 'react-confetti';

import { Button } from '~/domains/design-system/Button';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import { parseRewardsToPromoTickets } from '~/domains/promotions/utils/parsePromoTickets';
import { PromoTicketCard } from '~/domains/promotions/domains/promoCodes/components/PromoTicketCard/PromoTicketCard';
import { BonusCountdown } from '~/domains/promotions/components/BonusCountdown/BonusCountdown';
import dt from '~/testing';

import type { MissionDetailAssignment } from '../../types/missions.types';
import { MissionBadge } from '../MissionBadge/MissionBadge';

import { useStyles } from './MissionCompletedModal.styles';

export interface MissionCompletedModalProps {
  isOpen: boolean;
  isClosing: boolean;
  onClose: () => void;
  missionAssignment?: MissionDetailAssignment;
  isLoading?: boolean;
}

function MissionCompletedModal({
  isOpen,
  isClosing,
  onClose,
  missionAssignment,
  isLoading = false,
}: MissionCompletedModalProps) {
  const { classes } = useStyles();
  const { t } = useTranslation('promotions');
  const autoplay = useRef(Autoplay({ delay: 5000, stopOnInteraction: false }));

  const mission = missionAssignment?.mission;
  const tickets = mission?.rewards ? parseRewardsToPromoTickets(mission.rewards, t) : [];
  const showConfetti = isOpen && !isLoading && !!mission && typeof window !== 'undefined';

  return (
    <>
      {showConfetti && (
        <Confetti
          className={classes.confetti}
          recycle={false}
          numberOfPieces={1000}
          confettiSource={{
            x: window.innerWidth / 2 - 55,
            y: window.innerHeight / 2 - 100,
            w: 110,
            h: 100,
          }}
          tweenDuration={500}
          initialVelocityY={20}
          initialVelocityX={7.5}
        />
      )}
      <Dialog
        type={{
          desktop: 'modal',
          mobile: 'drawer',
        }}
        isOpen={isOpen}
        isClosing={isClosing}
        onClose={onClose}
        dataTestId={dt.loyalty.missions.completedModal.container}
      >
        <DialogBody className={classes.dialogBody}>
          {isLoading || !mission ? (
            <div className={classes.loaderWrap}>
              <Loader color="teal" />
            </div>
          ) : (
            <div className={classes.content}>
              <MissionBadge
                size="medium"
                rewardValue={mission.rewardValue}
                iconUrl={mission.iconUrl}
              />

              <div className={classes.header}>
                <Text variant="subhead-x-small" className={classes.eyebrow}>
                  {t('missionDetail.completedEyebrow')}
                </Text>
                <Title variant="title-xxl" tag="h2" className={classes.title}>
                  {t('missionDetail.completedTitle')}
                </Title>
              </div>

              <Text variant="title-medium" className={classes.description}>
                {mission.description}
              </Text>

              {tickets.length > 0 && (
                <div className={classes.ticketContainer}>
                  <Carousel
                    align="center"
                    slideSize={273}
                    /* eslint-disable-line local-rules/use-theme-tokens */ slideGap={16}
                    draggable={tickets.length > 1}
                    speed={3}
                    withIndicators={false}
                    withControls={false}
                    plugins={tickets.length > 1 ? [autoplay.current] : []}
                    classNames={{
                      viewport: classes.viewport,
                      container: classes.carouselContainer,
                    }}
                  >
                    {tickets.map((ticket) => (
                      <Carousel.Slide key={ticket.id}>
                        <PromoTicketCard {...ticket} />
                      </Carousel.Slide>
                    ))}
                  </Carousel>
                </div>
              )}

              <div className={classes.countdownContainer}>
                <BonusCountdown
                  expirationDate={mission.endsAt ? new Date(mission.endsAt) : undefined}
                />
              </div>

              <Button
                variant="secondary-fill"
                size="large"
                fullWidth
                className={classes.cta}
                onClick={onClose}
                data-test-id={dt.loyalty.missions.completedModal.gotItButton}
              >
                {t('missionDetail.gotIt')}
              </Button>
            </div>
          )}
        </DialogBody>
      </Dialog>
    </>
  );
}

export default MissionCompletedModal;
